import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { normalizeBoardMembers, seededBoardMembers } from '../data/boardMembers';
import { useSiteAuth } from '../hooks/useSiteAuth';
import {
  createBlissBoardMember,
  deleteBlissBoardMember,
  listBlissBoardMembers,
  updateBlissBoardMember,
} from '../services/board-members-api';
import type { BoardMemberRecord } from '../types';
import { BoardMemberContext } from './boardMemberContext';

const LOCAL_BOARD_MEMBERS_KEY = 'victorias-place-local-board-members';
const seededBoardMembersById = new Map(seededBoardMembers.map((member) => [member.id, member]));
const seededVisibleBoardMembers = normalizeBoardMembers(seededBoardMembers);

function applyLocalBoardMemberMigrations(members: BoardMemberRecord[]): BoardMemberRecord[] {
  return members.map((member) => {
    if (member.id !== 'board-melanie-ray') {
      return member;
    }

    const seededMember = seededBoardMembersById.get(member.id);
    if (!seededMember) {
      return member;
    }

    return {
      ...member,
      photo_url: seededMember.photo_url,
      photo_alt: seededMember.photo_alt,
    };
  });
}

function readLocalBoardMembers(): BoardMemberRecord[] {
  if (typeof window === 'undefined') {
    return normalizeBoardMembers(seededBoardMembers);
  }

  try {
    const raw = window.localStorage.getItem(LOCAL_BOARD_MEMBERS_KEY);
    if (!raw) {
      return normalizeBoardMembers(seededBoardMembers);
    }

    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? normalizeBoardMembers(applyLocalBoardMemberMigrations(parsed as BoardMemberRecord[]))
      : normalizeBoardMembers(seededBoardMembers);
  } catch {
    return normalizeBoardMembers(seededBoardMembers);
  }
}

function writeLocalBoardMembers(members: BoardMemberRecord[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    LOCAL_BOARD_MEMBERS_KEY,
    JSON.stringify(normalizeBoardMembers(members)),
  );
}

export function BoardMemberProvider({ children }: { children: ReactNode }) {
  const { session, runtime, runtimeError, isInitializing } = useSiteAuth();
  const [members, setMembers] = useState<BoardMemberRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [storageMode, setStorageMode] = useState<'bliss' | 'local'>('local');

  const includeAll = session?.role === 'admin' || session?.role === 'board_member';

  useEffect(() => {
    let isActive = true;

    async function loadMembers() {
      if (isInitializing) {
        return;
      }

      if (!runtime) {
        if (isActive) {
          setMembers(readLocalBoardMembers());
          setStorageMode('local');
          setSyncError(runtimeError);
          setIsLoading(false);
        }
        return;
      }

      if (isActive) {
        setIsLoading(true);
      }

      try {
        const nextMembers = await listBlissBoardMembers(includeAll);
        const normalizedMembers = normalizeBoardMembers(nextMembers);

        if (!normalizedMembers.length) {
          if (isActive) {
            setMembers(seededVisibleBoardMembers);
            setStorageMode('local');
            setSyncError(
              includeAll
                ? 'Bliss did not return any board member profiles. Showing the seeded public roster until the connected board records are available.'
                : 'No public board member profiles were returned from Bliss. Showing the seeded public roster instead.',
            );
          }
          return;
        }

        if (isActive) {
          setMembers(normalizedMembers);
          setStorageMode('bliss');
          setSyncError(null);
        }
      } catch (error) {
        if (includeAll) {
          try {
            const publicMembers = await listBlissBoardMembers(false);
            const normalizedPublicMembers = normalizeBoardMembers(publicMembers);

            if (isActive && normalizedPublicMembers.length) {
              setMembers(normalizedPublicMembers);
              setStorageMode('bliss');
              setSyncError(
                error instanceof Error
                  ? `${error.message} Showing the public board roster instead.`
                  : 'Board manager access to the full roster was denied. Showing the public board roster instead.',
              );
              return;
            }
          } catch {
            // Fall through to seeded local roster below.
          }
        }

        if (isActive) {
          setMembers(seededVisibleBoardMembers);
          setStorageMode('local');
          setSyncError(
            error instanceof Error
              ? `${error.message} Showing the seeded public roster instead.`
              : 'The board member workspace fell back to the seeded public roster.',
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    void loadMembers();

    return () => {
      isActive = false;
    };
  }, [includeAll, isInitializing, runtime, runtimeError]);

  const publicMembers = useMemo(
    () => members.filter((member) => member.is_visible),
    [members],
  );

  function commit(nextMembers: BoardMemberRecord[]) {
    const normalized = normalizeBoardMembers(nextMembers);
    setMembers(normalized);
    if (storageMode === 'local') {
      writeLocalBoardMembers(normalized);
    }
  }

  async function saveMember(member: BoardMemberRecord) {
    const normalizedMember = normalizeBoardMembers([member])[0];

    if (storageMode === 'bliss' && runtime) {
      const exists = members.some((current) => current.id === normalizedMember.id);
      const savedMember = exists
        ? await updateBlissBoardMember(normalizedMember)
        : await createBlissBoardMember(normalizedMember);
      const reloadedMembers = await listBlissBoardMembers(includeAll);
      commit(reloadedMembers);
      setSyncError(null);
      return reloadedMembers.find((current) => current.id === savedMember.id) ?? savedMember;
    }

    commit(
      members.some((current) => current.id === normalizedMember.id)
        ? members.map((current) =>
            current.id === normalizedMember.id ? normalizedMember : current,
          )
        : [...members, normalizedMember],
    );
    return normalizedMember;
  }

  async function deleteMember(memberId: string) {
    if (storageMode === 'bliss' && runtime) {
      await deleteBlissBoardMember(memberId);
      const reloadedMembers = await listBlissBoardMembers(includeAll);
      commit(reloadedMembers);
      setSyncError(null);
      return;
    }

    commit(members.filter((member) => member.id !== memberId));
  }

  return (
    <BoardMemberContext.Provider
      value={{
        isLoading,
        syncError,
        storageMode,
        members,
        publicMembers,
        saveMember,
        deleteMember,
      }}
    >
      {children}
    </BoardMemberContext.Provider>
  );
}
